import type { CacheProperties } from 'axios-cache-interceptor';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';

import type { DeepPartial } from '../../ts/index';

import type { ApiActionLogger } from './types/ApiActionLogger';
import type { FetchStateCacheConfig } from './types/CacheConfig';
import type { FetchConfig } from './types/FetchConfig';
import type { CacheRequestConfigWithHelpers, Fetcher } from './types/Fetcher';
import type { FetchStateCallback } from './types/FetchStateCallbacks';
import type { FetchStateConfig } from './types/FetchStateConfig';
import type { FetchStateDataSourceSpecification } from './types/FetchStateDataSourceSpecification';
import type { FetchStateGlobalMiddleware } from './types/FetchStateGlobalMiddleware';
import type { LoadingState } from './types/LoadingState';

export class FetchState<TResponse = unknown, TError = unknown, TExtractedData = unknown> implements LoadingState {
  private static _runningInstances: Set<FetchState> = new Set();
  private static _dataSources: FetchStateDataSourceSpecification<any>[] = [];
  private static _middlewares: Map<string, FetchStateGlobalMiddleware> = new Map();

  private callbacks: FetchStateCallback<TResponse, NonNullable<typeof this.error>, TExtractedData> | undefined;
  private cacheConfig: FetchStateCacheConfig | undefined;
  protected logger: ApiActionLogger | undefined;
  protected id: string | undefined;
  private ac = new AbortController();
  private _latestFetchKey: string | null = null;
  private _activePromises: Set<Promise<any>> = new Set();
  private _isTimeoutCritical = true;
  private _isCancelledError: ((error: unknown) => boolean) | undefined;
  private _isTimeoutError: ((error: unknown) => boolean) | undefined;
  private _defaultCacheTtl: number | undefined;

  suspensePromise: Promise<any> | null = null;

  isLoading = false;

  isError = false;

  isSuccess = false;

  isRefetching = false;

  error: TError | null | undefined;

  response: TResponse | undefined;

  data: TExtractedData | undefined;

  isInitLoading = true;

  previouslyCancelled = false;

  static get middlewares() {
    return this._middlewares;
  }

  get latestFetchKey() {
    return this._latestFetchKey;
  }

  get activePromises() {
    return toJS(this._activePromises);
  }

  get isOngoing() {
    return Boolean(this.activePromises.size) || this.isLoading || this.isRefetching;
  }

  /**
   * @deprecated Backtracking support constructor
   */
  constructor(logger: ApiActionLogger, id?: string, ac?: AbortController);
  constructor(config?: FetchStateConfig);
  constructor(config?: FetchStateConfig | ApiActionLogger, id?: string, ac?: AbortController) {
    if (config && 'logger' in config) {
      this.logger = config?.logger;
      this.id = config?.id;
      this._isTimeoutCritical = config?.isTimeoutCritical ?? true;
      this.cacheConfig = config?.cache;
      this._isCancelledError = config?.isCancelledError;
      this._isTimeoutError = config?.isTimeoutError;
      this._defaultCacheTtl = config?.defaultCacheTtl;
    } else if (config && !('success' in config)) {
      this.cacheConfig = (config as FetchStateConfig)?.cache;
      this._isCancelledError = (config as FetchStateConfig)?.isCancelledError;
      this._isTimeoutError = (config as FetchStateConfig)?.isTimeoutError;
      this._defaultCacheTtl = (config as FetchStateConfig)?.defaultCacheTtl;
    }

    if (config && 'success' in config) {
      this.logger = config;
      this.id = id;
      this.ac = ac ?? new AbortController();
    }

    makeObservable(this, {
      // @ts-expect-error should cover private field
      _activePromises: observable,
      _latestFetchKey: observable,
      //
      suspensePromise: observable,
      activePromises: computed,
      latestFetchKey: computed,
      isOngoing: computed,
      isLoading: observable,
      isError: observable,
      isSuccess: observable,
      isRefetching: observable,
      error: observable,
      data: observable,
      isInitLoading: observable,
      previouslyCancelled: observable,
      fetch: action,
      runInRefetchTransaction: action,
      abort: action,
      wipeData: action,
    });
  }

  get cacheKey() {
    return this.cacheConfig?.key;
  }

  static combineLoadingState = (args: FetchState<any, any, any>[]) => {
    return args.reduce((acc, curr) => {
      return {
        isLoading: acc.isLoading || curr.isLoading,
        isError: acc.isError || curr.isError,
        isSuccess: acc.isSuccess || curr.isSuccess,
        isRefetching: acc.isRefetching || curr.isRefetching,
        isInitLoading: acc.isInitLoading && curr.isInitLoading,
      };
    }, {} as LoadingState);
  };

  static killAll = (args?: { keep?: string[] }) => {
    const { keep } = args || {};
    const idFiltersSet = new Set(keep || []);

    this._runningInstances.forEach((instance) => {
      if (!idFiltersSet.has(instance.getId()!)) {
        instance.abort();
      }
    });

    this._runningInstances.clear();
  };

  static killScope = (scope: object) => {
    Object.values(scope).forEach((value) => {
      if (value instanceof FetchState) {
        value.abort();
      }
    });
  };

  static configureDataSource<TResponse>(dataSourceSpecification: FetchStateDataSourceSpecification<TResponse>) {
    FetchState._dataSources.push(dataSourceSpecification);
  }

  static middleware(key: string, config: FetchStateGlobalMiddleware) {
    this._middlewares.set(key, config);

    return () => this._middlewares.delete(key);
  }

  async fetch(fetcher: Fetcher<TResponse>, config?: FetchConfig<TResponse, TError>): Promise<TResponse> {
    FetchState.addRunningInstance(this);

    let result: TResponse = {} as never;
    let scopedPromise: Promise<TResponse>;

    try {
      this.fetchStartHandler();

      const cacheRequestConfig = this.buildCacheRequestConfig();
      scopedPromise = fetcher(this.ac.signal, cacheRequestConfig);
      this._activePromises.add(scopedPromise);
      this.updateSuspensePromise();
      result = await scopedPromise;

      const dataSource = this.prepareDataSource(result);
      if (dataSource.isSuccess(result)) {
        this.successHandler(result, dataSource.data(result), config);
        return result;
      }
      this.errorHandler(dataSource.error(result), dataSource.data(result), config);
    } catch (err) {
      this.errorHandler(err as NonNullable<typeof this.error>);

      return result;
    } finally {
      runInAction(() => {
        this._activePromises.delete(scopedPromise);
        this.updateSuspensePromise();
      });
      runInAction(() => (this.isInitLoading = false));
      runInAction(() => (this._latestFetchKey = crypto.randomUUID()));

      FetchState.removeRunningInstance(this);
    }

    return result;
  }

  setCallbacks(callbacks: typeof this.callbacks) {
    this.callbacks = callbacks;
  }

  runInRefetchTransaction = action('Refetch action started', async (callback: Promise<unknown>) => {
    runInAction(() => (this.isRefetching = true));
    await callback;
    runInAction(() => (this.isRefetching = false));
  });

  abort = action('Request abort', () => {
    this.ac.abort('Request aborted by FetchState');
    this._activePromises.clear();
    this.suspensePromise = null;
    this.previouslyCancelled = true;
    this.ac = new AbortController();
  });

  wipeData() {
    this.isLoading = false;
    this.isError = false;
    this.isSuccess = false;
    this.callbacks = {};
    this.isInitLoading = false;
    this.error = undefined;
    this.data = undefined;
    this.response = undefined;
  }

  /**
   * Manually sets fetch state into the loading position. Returns a disposer that MUST be called.
   */
  wait = () => {
    const { promise: haltSignal, resolve } = Promise.withResolvers();

    runInAction(() => {
      this._activePromises.add(haltSignal);
      this.isLoading = true;
    });

    return () => {
      runInAction(() => {
        this.isLoading = false;
        resolve(null as never);
        this._activePromises.delete(haltSignal);
      });
    };
  };

  triggerSuccess = (args: { withData: TResponse }) => {
    const { withData } = args;

    const dataSource = this.prepareDataSource(withData);
    this.successHandler(withData, dataSource.data(withData));

    return this;
  };

  static executeMiddlewaresWith(args: { error?: unknown; success?: unknown }) {
    let isErrorHandled = false;
    let isSuccessHandled = false;

    const middlewaresArray = Array.from(this._middlewares.values());
    const bottomToTopMiddlewares = middlewaresArray.reverse();

    bottomToTopMiddlewares.forEach((m) => {
      if (args.error !== null && args.error !== undefined) {
        const { isHandled: isMiddlewareErrorHandled } = m.onError?.(args.error, args.success, isErrorHandled) || {};
        const isIgnoredReturnType = isMiddlewareErrorHandled === undefined;
        isErrorHandled = isIgnoredReturnType ? true : Boolean(isErrorHandled || isMiddlewareErrorHandled);
      }

      if (args.success !== null && args.success !== undefined) {
        const { isHandled: isMiddlewareSuccessHandled } = m.onSuccess?.(args.success, isSuccessHandled) || {};
        const isIgnoredReturnType = isMiddlewareSuccessHandled === undefined;
        isSuccessHandled = isIgnoredReturnType ? true : Boolean(isErrorHandled || isMiddlewareSuccessHandled);
      }
    });
  }

  copy() {
    const config: FetchStateConfig = {};
    if (this.logger) config.logger = this.logger;
    if (this._isCancelledError) config.isCancelledError = this._isCancelledError;
    if (this._isTimeoutError) config.isTimeoutError = this._isTimeoutError;
    if (this._defaultCacheTtl !== undefined) config.defaultCacheTtl = this._defaultCacheTtl;

    return new FetchState<TResponse, TError, TExtractedData>(config);
  }

  getId() {
    return this.id;
  }

  private buildCacheRequestConfig(): CacheRequestConfigWithHelpers {
    if (!this.cacheConfig?.enabled) {
      return { cache: false };
    }

    const cacheProperties: Partial<CacheProperties> = {};
    const ttl = this.cacheConfig.ttl ?? this._defaultCacheTtl;
    if (ttl !== undefined) {
      cacheProperties.ttl = ttl;
    }

    if (this.cacheConfig.cachePredicate) {
      cacheProperties.cachePredicate = {
        responseMatch: (response: any) => this.cacheConfig!.cachePredicate!(response.data),
      };
    }

    return {
      cache: cacheProperties,
      _cacheOptIn: true,
      id: this.cacheConfig.key,
      processCacheId(delegate: (k: string | String | undefined) => string) {
        return {
          ...this,
          id: delegate(this.id),
        };
      },
    } as CacheRequestConfigWithHelpers;
  }

  private updateSuspensePromise() {
    this.suspensePromise =
      this._activePromises.size === 0
        ? null
        : Promise.allSettled([...this._activePromises]);
  }

  private prepareDataSource(response: TResponse) {
    const dataSource = FetchState._dataSources.find((x) => x.is(response));

    if (!dataSource)
      throw new Error(
        `Data source for such response doesn't specified: ${JSON.stringify(response, null, 2)}. Ensure to use one of the following formats (or specify another data source): ${JSON.stringify(FetchState._dataSources, null, 2)}`,
      );

    return dataSource;
  }

  private fetchStartHandler = () => {
    this.isLoading = true;
    this.isError = false;
    this.isSuccess = false;
    this.error = null;
  };

  private async successHandler(
    response: TResponse,
    extractedData: TExtractedData,
    config?: FetchConfig<TResponse, TError>,
  ) {
    const isPipeIgnored = Boolean(config?.ignoreSuccessPipe?.(response as DeepPartial<TResponse>));

    runInAction(async () => {
      this.data = extractedData;
      this.response = response;
      this.isSuccess = true;
      this.isLoading = false;
      this.previouslyCancelled = false;
      const callbackResult = await this.callbacks?.onSuccess?.(response, extractedData);

      const customSuccessMessage = callbackResult?.loggerMessage;
      const defaultMessage = this.logger?.defaultMappers.success?.(response);

      !isPipeIgnored && FetchState.executeMiddlewaresWith({ success: response });
      this.logger?.success(customSuccessMessage || defaultMessage);
    });
  }

  private errorHandler(
    error: NonNullable<typeof this.error>,
    data: unknown = null,
    config?: FetchConfig<TResponse, TError>,
  ) {
    const isPipeIgnored = Boolean(config?.ignoreErrorPipe?.(error as DeepPartial<TError>));

    runInAction(() => {
      this.isError = true;
      this.isLoading = false;
      this.error = error;
      const callbackResult = this.callbacks?.onError?.(error);

      const customErrorMessage = callbackResult?.loggerMessage;
      const defaultMessage = this.logger?.defaultMappers.error?.(error);

      if (!this._isCancelledError?.(error)) {
        this.previouslyCancelled = false;
        !isPipeIgnored && FetchState.executeMiddlewaresWith({ error, success: data });
        this.logger?.error(customErrorMessage || defaultMessage);
      }

      if (this._isTimeoutError?.(error)) {
        (error as { critical?: boolean }).critical = this._isTimeoutCritical;
      }
    });
  }

  private static removeRunningInstance(instance: FetchState<any, any, any>) {
    this._runningInstances.delete(instance);
  }

  private static addRunningInstance(instance: FetchState<any, any, any>) {
    this._runningInstances.add(instance);
  }
}
